// dependencies
import Immutable, { isImmutable as checkIsImmutable, fromJS } from "immutable";
import { nanoid } from "nanoid/non-secure";
import React, { ClassAttributes, HTMLAttributes, PropsWithChildren, useMemo, useRef } from "react";
import Select, {
	FormatOptionLabelContext,
	FormatOptionLabelMeta,
	GroupBase,
	MenuPosition,
	Props,
	ValueContainerProps,
	components,
} from "react-select";
import { useResizeObserver } from "usehooks-ts";

import { faAngleDown, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// config
import Config from "./Config";

import Chip from "../../Chip";
import DeletableChip from "../../Chip/templates/DeletableChip";
// components
import Text, { BaseProps as TextProps } from "../../Text";
import TruncatedText from "../../Text/TruncatedText";
import InputWrapper from "../InputWrapper";
// utils
import { Info } from "../InputWrapper";
// styles
import css from "./SelectInput.module.scss";

interface ImmutableResult {
	id: string;
	label: string;
}

export interface Result {
	label: string;
	// non-grouped options take in a single string value
	value?: string | number | null;
	// grouped options takes in another options array
	options?: {
		label: string;
		value: string;
	}[];
	// fixedText is used to display a fixed text after the truncated text
	fixedText?: string;
}

type OptionProps = PropsWithChildren<{
	/**
	 * The ref to the inner div,
	 * needed for react-select interactivity
	 */
	innerRef: (instance: HTMLDivElement | null) => void;
	/**
	 * The props to pass to the inner div,
	 * needed for react-select interactivity
	 */
	innerProps: HTMLAttributes<HTMLDivElement>;
	selectProps: Props;
	/**
	 * The data for the option, useful if you want
	 * to render something derived from the label/value
	 */
	data: Result;
}>;

type OptionComponent = React.FC<PropsWithChildren<ClassAttributes<OptionProps>>>;

export interface BaseProps {
	/**
	 * The label to display above the input. This label will have a `for`
	 * attribute to specify which input it is bound to.
	 */
	label?: string;
	/**
	 * Typography Props
	 */
	labelProps?: TextProps;
	/**
	 * An optional info message to display when hovered over the info icon.
	 */
	info?: Info;
	/**
	 * Whether or not the input is required.
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * The class name to apply to the input.
	 * @default undefined
	 */
	className?: string;
	/**
	 * The name of the input.
	 * @default undefined
	 */
	name?: string;
	/**
	 * The id of the input. If not provided, a random id will be generated
	 * by nanoid.
	 * @default undefined
	 */
	inputId?: string;
	/**
	 * The options to display in the dropdown.
	 */
	results: Result[] | Immutable.List<ImmutableResult>;
	/**
	 * The value of the input. For single select, this is a string. For
	 * multi select, this is an array of strings.
	 */
	value?: string | string[] | Immutable.Map<string, string> | Result | Result[] | null;
	/**
	 * Whether or not there are multiple options that can be selected.
	 * @default false
	 */
	isMultiSelect?: boolean;
	/**
	 * The function to call when the input value changes.
	 */
	onChange: (...args: any[]) => void;
	/**
	 * The function to call when the input value changes.
	 */
	handleInputChange?: (...args: any[]) => void;
	/**
	 * The function to call when the focused element changes.
	 */
	onChangeFocusedElement?: (optionIndex: number) => void;
	/**
	 * Whether or not to set the first result as the focused element.
	 * @default true
	 */
	setFirstResult?: boolean;
	/**
	 * Whether or not the input is searchable.
	 * @default true
	 */
	isSearchable?: boolean;
	/**
	 * Override the built-in logic to detect whether an option is disabled.
	 * @default () => false
	 * @see https://react-select.com/props#select-props
	 */
	isOptionDisabled?: () => boolean;
	/**
	 * Whether or not the input is clearable. Renders a clear button when true.
	 * @default false
	 */
	isClearable?: boolean;
	/**
	 * The size of the input.
	 */
	size?: "standard" | "small";
	/**
	 * If cacheOptions is truthy, then the loaded data will be cached. The
	 * cache will remain until cacheOptions changes value.
	 * @default true
	 * @see https://react-select.com/props#select-props
	 */
	cacheOptions?: any;
	/**
	 * Whether the menu should use a portal, and where it should attach.
	 * @default null
	 * @see https://react-select.com/props#select-props
	 * @see https://react-select.com/advanced#portaling
	 */
	menuPortalTarget?: any;
	/**
	 * menu position to be set to absolute when using portal
	 */
	menuPosition?: MenuPosition;
	/**
	 * menu placement to be set to bottom when using portal
	 */
	menuPlacement?: "bottom" | "top" | "auto";
	/**
	 * The placeholder text to display when there is no value.
	 * @default null
	 */
	placeholder?: string;
	onFocus?: () => void;
	/**
	 * Set specific styles to the select input
	 */
	formatOptionLabel?: (
		data: any,
		formatOptionLabelMeta: FormatOptionLabelMeta<FormatOptionLabelContext>
	) => React.ReactNode;
	/**
	 * Bypass the default filter logic. Useful for custom filtering.
	 */
	filterOption?: ((option: Result, rawInput: string) => boolean) | null;
	/**
	 * Whether or not the input is loading.
	 */
	isLoading?: boolean;

	/**
	 * The max height of the menu
	 */
	maxMenuHeight?: number;

	/**
	 *	The title image
	 */
	titleImage?: string;

	/**
	 *	href  of the title
	 */
	titleHref?: string;
	/**
	 * title tooltip
	 */
	titleTooltip?: string;
	/**
	 * Error message to display
	 */
	errorMessage?: string;
}

const formatGroupLabel = (data: GroupBase<Result>) => (
	<div className={css.optionGroup}>
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			{data.label}
		</Text>
		<Text className={css.groupBadge} size="caption" weight="semi-bold" color="neutral-9">
			{data.options.length}
		</Text>
	</div>
);

const DropdownIndicator = (props: any) => {
	return (
		<components.DropdownIndicator {...props}>
			<FontAwesomeIcon icon={faAngleDown} />
		</components.DropdownIndicator>
	);
};

// custom clear indicator when select input is clearable
const ClearIndicator = (props: any) => {
	return (
		<components.ClearIndicator {...props}>
			<FontAwesomeIcon icon={faCircleXmark} />
		</components.ClearIndicator>
	);
};
/**
 * The Select Input component is a wrapper around the react-select component.
 * @deprecated Use the Select/Combobox components from the py-components package instead
 */
const SelectInput = (props: BaseProps) => {
	const {
		name,
		label,
		labelProps = {
			size: "caption",
			weight: "regular",
		},
		info,
		value,
		results,
		disabled,
		onChange,
		onChangeFocusedElement,
		isMultiSelect = false,
		setFirstResult = true,
		isSearchable = true,
		isOptionDisabled = () => false,
		isClearable = false,
		menuPortalTarget = null,
		menuPosition = undefined,
		menuPlacement = undefined,
		placeholder = null,
		handleInputChange,
		onFocus = () => {},
		formatOptionLabel,
		filterOption,
		isLoading = false,
		maxMenuHeight,
		titleImage,
		titleHref,
		titleTooltip,
		errorMessage,
	} = props;

	const size = props.size || "standard";
	const isImmutable = checkIsImmutable(results);
	const pickerRef = useRef<HTMLDivElement>(null);
	const { width } = useResizeObserver<HTMLDivElement>({
		ref: pickerRef,
	});

	const inputId = useMemo(() => {
		return props.inputId || nanoid();
	}, [props.inputId]);

	let options: Immutable.List<ImmutableResult> | Result[] = [];

	// if results is immutable, convert it to proper react select options array
	if (isImmutable) {
		const immutableResults = fromJS(results) as Immutable.List<ImmutableResult>;
		options = immutableResults.toJS().map((result: any) => {
			return { value: result.id, label: result.label };
		});
		console.warn("SelectInput: results are immutable");
	} else {
		options = results;
	}

	const hasGroups = options.some((option) => option.options);

	const getValue = () => {
		// handle case where value is string
		if (typeof value === "string" || typeof value === "number") {
			return options.find((option) => option.value === value) || "";
		}
		// handle case where value is immutable map
		else if (value instanceof Immutable.Map) {
			const immutableValue = value as Immutable.Map<string, string>;
			return {
				value: immutableValue.get("id"),
				label: immutableValue.get("label"),
			};
		} else if (Array.isArray(value)) {
			return value.map((val) => {
				if (typeof val === "string" || val === null) {
					return options.find((option) => option.value === val);
				}
				return val;
			});
		}
		return value;
	};

	const handleChange = (val: Result | Result[]) => {
		if (isImmutable) {
			const immutableValue = val as Result;
			onChange(immutableValue.value, immutableValue.label);
		} else {
			onChange(val);
		}
	};

	const Option = (localProps) => {
		const optionIndex = options.findIndex((option) => option.value === localProps.value);

		return (
			<div
				className={`${css.optionWrapper} ${hasGroups ? css.optionWrapperGrouped : ""}`}
				onMouseEnter={() => {
					if (onChangeFocusedElement) {
						onChangeFocusedElement(optionIndex);
					}
				}}
			>
				<components.Option {...localProps} />
			</div>
		);
	};

	const TruncatedOption: OptionComponent = (localProps: any) => {
		const {
			innerProps,
			innerRef,
			data: { label },
		} = localProps;

		const nodeRef = useRef<HTMLDivElement>(null);
		const { width = 1 } = useResizeObserver({ ref: nodeRef });

		return (
			<div
				ref={innerRef}
				{...innerProps}
				className={`${css.truncatedOptionWrapper} ${css.optionWrapper} ${
					hasGroups ? css.optionWrapperGrouped : ""
				}`}
			>
				<div ref={nodeRef}>
					<TruncatedText
						tooltipPlacement="right"
						strategy="fixed"
						text={label}
						maxWidth={width}
						size="caption"
						weight="regular"
					/>
				</div>
			</div>
		);
	};

	const TruncatedValueContainer = (props: ValueContainerProps<Result>) => {
		const { children, ...innerProps } = props;
		const nodeRef = useRef<HTMLDivElement>(null);
		const { width = 1 } = useResizeObserver({ ref: nodeRef });
		const value = children?.[0]?.props?.data?.label?.toString();
		const input = children?.[1];
		return (
			<components.ValueContainer {...innerProps}>
				<div className={css.truncatedValueContainer} ref={nodeRef}>
					<div className={css.inputValue}>{input}</div>
					<TruncatedText
						tooltipPlacement="right"
						className={css.truncatedText}
						strategy="fixed"
						text={value}
						maxWidth={width}
						size="caption"
						weight="regular"
					/>
				</div>
			</components.ValueContainer>
		);
	};

	// delete function for multivalue options
	const handleDelete = (valToDelete?: string | number | null) => {
		const multiValues = (getValue() as Result[]) || [];
		if (valToDelete) {
			const filteredValues = multiValues.filter((val) => val.value !== valToDelete);
			onChange(filteredValues);
		}
	};

	const selectValue = getValue();
	const multiValues = (selectValue as Result[]) || [];

	const styles = useMemo(() => {
		return Config.customStyles({ size, showWarning: !!errorMessage });
	}, [size, errorMessage]);

	return (
		<InputWrapper {...{ ...labelProps, info, label, inputId, titleHref, titleImage, titleTooltip, errorMessage }}>
			<div ref={pickerRef} data-testid={name ? `select-${name}` : "select"}>
				<Select
					name={name}
					className={`${hasGroups ? css.selectWithGroups : css.select}`}
					placeholder={placeholder === null ? "Select..." : placeholder}
					inputId={inputId}
					defaultValue={setFirstResult ? options[0] || null : null}
					styles={styles}
					value={selectValue}
					options={options as Result[]}
					formatGroupLabel={formatGroupLabel}
					isDisabled={disabled}
					onChange={handleChange}
					onInputChange={handleInputChange ? handleInputChange : () => {}}
					isMulti={isMultiSelect}
					components={{
						ValueContainer: size === "small" ? TruncatedValueContainer : components.ValueContainer,
						Option: size === "small" ? TruncatedOption : Option,
						DropdownIndicator,
						ClearIndicator,
					}}
					filterOption={filterOption}
					isSearchable={isSearchable}
					isOptionDisabled={isOptionDisabled}
					isClearable={isClearable}
					controlShouldRenderValue={!isMultiSelect}
					menuPortalTarget={menuPortalTarget}
					menuPosition={menuPosition}
					menuPlacement={menuPlacement}
					onFocus={onFocus}
					formatOptionLabel={formatOptionLabel}
					isLoading={isLoading}
					maxMenuHeight={maxMenuHeight}
				/>
				{isMultiSelect && (
					<div className={css.multiSelectOptions}>
						{multiValues.map((value) => {
							const ChipComponent = disabled ? Chip : DeletableChip;
							return (
								<ChipComponent
									className={css.multiValueOption}
									key={value.value}
									label={
										<TruncatedText
											useTooltip="never"
											text={value.label}
											maxWidth={width - 48}
											size="caption"
											weight="semi-bold"
										/>
									}
									onDelete={() => handleDelete(value.value)}
								/>
							);
						})}
					</div>
				)}
			</div>
		</InputWrapper>
	);
};

export default SelectInput;
